/**
 * @param string value
 * @returns string
 */
export function padWithZero(value) {
    return value < 10 ? `0${value}` : value;
}

/**
 * @param string|Date date
 * @returns string
 */
export function getFormatedDate(date) {
    if (date instanceof Date == false) {
        date = new Date(date);
    }
    return `${date.getFullYear()}-${padWithZero(date.getMonth() + 1)}-${padWithZero(date.getDate())}`;
}

/**
 * @returns boolean
 */
export function hasAuthToken() {
    const authToken = document.cookie.match('(^|;)\\s*pelcro.user.auth.token\\s*=\\s*([^;]+)')?.pop() || '';
    return authToken !== '';
}

/**
 * @returns boolean
 */
export function isUserLoggedIn() {
    let loggedIn = false;
    if (window.Pelcro && 'user' in window.Pelcro) {
        loggedIn = window.Pelcro.user.isAuthenticated();
    } else {
        loggedIn = hasAuthToken();
    }

    return loggedIn;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns boolean
 */
export function isUserEntitledTo(entitlement) {
    let entitledTo = false;
    if (window.Pelcro && 'user' in window.Pelcro) {
        entitledTo = window.Pelcro.user.isEntitledTo(entitlement);
    }

    return entitledTo;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns boolean
 */
export function isUserSubscribed() {
    let subscribed = false;
    if (window.Pelcro && 'subscription' in window.Pelcro) {
        subscribed = window.Pelcro.subscription.isSubscribedToSite();
    }

    return subscribed;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns boolean
 */
export function isUserNewsletterSubscribed() {
    let newsletterSubscribed = false;
    if (window.Pelcro && 'newsletter' in window.Pelcro) {
        newsletterSubscribed = window.Pelcro.newsletter.isSubscribedToNewsletter();
    }

    return newsletterSubscribed;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns array
 */
export async function getUserNewsletterSubscribed() {
    let newsletterSubscribed = [];
    if (window.Pelcro && 'newsletter' in window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();
        if ('email' in user) {
            await window.Pelcro.newsletter.getByEmail(user.email, (error, response) => {
                if (error) {
                    return console.log('Pelcro Newsletter:', error.message);
                }

                if ('lists' in response && Array.isArray(response.lists)) {
                    newsletterSubscribed = response.lists;
                }
            });
        }
    }

    return newsletterSubscribed;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns boolean
 */
export function hasUserPurchased() {
    let purchased = false;
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();
        purchased = 'orders' in user && user.orders.length > 0;
    }

    return purchased;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns string
 */
export function getUserSubscriptionType() {
    let subscriptionType = 'NoSub';
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();

        if ('subscriptions' in user && Array.isArray(user.subscriptions) && user.subscriptions.length > 0) {
            const subscriptionTypes = user.subscriptions.map(userSubscription => {
                if (
                    userSubscription.status == 'active' &&
                    (userSubscription.plan.nickname.toLowerCase().includes('bundle') ||
                        userSubscription.plan.product.name.toLowerCase().includes('bundle'))
                ) {
                    return 'Bundle';
                } else if (
                    userSubscription.status == 'active' &&
                    (userSubscription.plan.nickname.toLowerCase().includes('digital') ||
                        userSubscription.plan.product.name.toLowerCase().includes('digital'))
                ) {
                    return 'Digital';
                }
            });
            subscriptionType = [...new Set(subscriptionTypes)].join(',');
        }
    }

    return subscriptionType;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns string
 */
export function getUserSubscriptionStatus() {
    let subscriptionStatus = 'false';
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();

        if ('subscriptions' in user && Array.isArray(user.subscriptions) && user.subscriptions.length > 0) {
            const subscriptionStatuses = user.subscriptions.map(userSubscription => userSubscription.status);

            // If there are multiple subscriptions we can only display one status so need to determine the order of importance
            if (subscriptionStatuses.includes('active')) {
                subscriptionStatus = 'active';
            } else if (subscriptionStatuses.includes('canceled')) {
                subscriptionStatus = 'canceled';
            } else if (subscriptionStatuses.includes('trialing')) {
                subscriptionStatus = 'trialing';
            } else if (subscriptionStatuses.includes('past_due')) {
                subscriptionStatus = 'past_due';
            } else if (subscriptionStatuses.includes('unpaid')) {
                subscriptionStatus = 'unpaid';
            } else if (subscriptionStatuses.includes('incomplete')) {
                subscriptionStatus = 'incomplete';
            } else if (subscriptionStatuses.includes('incomplete_expired')) {
                subscriptionStatus = 'incomplete_expired';
            }
        }
    }

    return subscriptionStatus;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns object
 */
export function getUserSubscriptionDates() {
    let subscriptionDates = {
        subscription_start_date: 'NA',
        subscription_renewal_date: 'NA',
        cancellation_date: 'NA'
    };
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();

        if ('subscriptions' in user && Array.isArray(user.subscriptions) && user.subscriptions.length > 0) {
            let userSubscriptions = user.subscriptions.filter(
                userSubscription => userSubscription.status == 'active' && 'created' in userSubscription && userSubscription.created !== null
            );
            if (!Array.isArray(userSubscriptions) || userSubscriptions.length <= 0) {
                userSubscriptions = user.subscriptions.filter(
                    userSubscription => userSubscription.status == 'canceled' && 'created' in userSubscription && userSubscription.created !== null
                );
            }

            if (Array.isArray(userSubscriptions) && userSubscriptions.length > 0) {
                userSubscriptions.sort(
                    (userSubscription1, userSubscription2) => new Date(userSubscription2.created).getTime() - new Date(userSubscription1.created).getTime()
                );

                if ('start' in userSubscriptions[0] && userSubscriptions[0].start !== null) {
                    subscriptionDates.subscription_start_date = getFormatedDate(userSubscriptions[0].start);
                }

                if ('renews_at' in userSubscriptions[0] && userSubscriptions[0].renews_at !== null) {
                    // Need to convert UNIX timestamp to format JS date can accept
                    subscriptionDates.subscription_renewal_date = getFormatedDate(parseInt(userSubscriptions[0].renews_at) * 1000);
                }

                if ('ended_at' in userSubscriptions[0] && userSubscriptions[0].ended_at !== null) {
                    subscriptionDates.cancellation_date = getFormatedDate(userSubscriptions[0].ended_at);
                } else if ('cancel_at' in userSubscriptions[0] && userSubscriptions[0].cancel_at !== null) {
                    subscriptionDates.cancellation_date = getFormatedDate(userSubscriptions[0].cancel_at);
                }
            }
        }
    }

    return subscriptionDates;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns string
 */
export function getUserCurrency() {
    let currency = '';
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();
        if ('currency' in user && user.currency !== null) {
            currency = user.currency.toUpperCase();
            if (currency !== '') {
                document.cookie = `pelcro-currency=${currency}; path=/; Secure`;
            }
        }
    }

    return currency;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns array
 */
export function getUserSubscriptionCodes() {
    let subscriptionCodes = [];
    if (window.Pelcro && 'user' in window.Pelcro) {
        const user = window.Pelcro.user.read();
        if ('subscriptions' in user && Array.isArray(user.subscriptions) && user.subscriptions.length > 0) {
            // We need to fetch the entitlements of each sub as that contains the top level product code e.g. PYWL23
            subscriptionCodes = user.subscriptions.map(userSubscription => {
                if (
                    'entitlements' in userSubscription.plan.product &&
                    Array.isArray(userSubscription.plan.product.entitlements) &&
                    userSubscription.plan.product.entitlements.length > 0 &&
                    userSubscription.status == 'active'
                ) {
                    return `${userSubscription.plan.product.entitlements}`;
                }
            });
        }
    }

    return subscriptionCodes;
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns void
 */
export function updateAdTargets() {
    const adTargets = document.querySelectorAll('[data-behaviour="ad-target"]');
    if (adTargets.length > 0) {
        if (isUserSubscribed()) {
            document.body.classList.add('subscriber');
        } else {
            document.body.classList.remove('subscriber');
        }

        if (isUserEntitledTo('noads')) {
            adTargets.forEach(adTarget => {
                const adContainer = adTarget.closest('[data-target="ad-container"]');
                if (adContainer !== null) {
                    adContainer.classList.add('u-hidden');
                    console.log('Ad Hide: ' + adTarget.getAttribute('id'));
                }
            });

            window.__ciads = {};
            document.body.classList.add('hide-ads');
            document.body.classList.remove('show-ads');
            document.body.classList.remove('limited-ads');
        } else if (isUserEntitledTo('limitedads')) {
            const billboardAd = document.querySelector('#div-gpt-ad-1563288058060-0');
            if (billboardAd !== null) {
                billboardAd.classList.add('u-hidden');
                console.log('Ad Hide: ' + billboardAd.getAttribute('id'));
            }

            document.body.classList.add('limited-ads');
            document.body.classList.remove('show-ads');
            document.body.classList.remove('hide-ads');
        } else {
            document.body.classList.add('show-ads');
            document.body.classList.remove('limited-ads');
            document.body.classList.remove('hide-ads');
        }
    }
}

/**
 * Only available one the PelcroUserLoaded event has triggered
 *
 * @returns void
 */
export function renderAds() {
    // Will need to wait till the user object has loaded before able to check if should display paywall tags
    if (hasAuthToken()) {
        document.addEventListener('PelcroUserLoaded', () => {
            updateAdTargets();
        });
    } else {
        document.addEventListener('PelcroSiteLoaded', () => {
            updateAdTargets();
        });
    }
}

/**
 * @returns string
 */
export function getPaywallType() {
    let paywallType = 'none';
    if (pelcro_details && 'paywall_type' in pelcro_details && pelcro_details.paywall_type != '') {
        paywallType = pelcro_details.paywall_type;
    }

    return paywallType;
}

/**
 * Get the number free page views left in paywall via cookie
 *
 * @returns {string}
 */
export function getPaywallPageViewsLeft() {
    return document.cookie.match('(^|;)\\s*pelcro_count_of_articles_left\\s*=\\s*([^;]+)')?.pop() || 1;
}

/**
 * Get the number free page views left in paywall
 *
 * @returns {integer}
 */
export function getPaywallFreeVisitsLeft() {
    let visitsLeft = 0;
    if (window.Pelcro && 'paywall' in window.Pelcro) {
        visitsLeft = window.Pelcro.paywall.freeVisitsLeft();
    }
    return parseInt(visitsLeft);
}

/**
 * This will increment the number free page views left in paywall
 */
export function incrementPaywallPageView() {
    if (window.Pelcro && 'paywall' in window.Pelcro) {
        window.Pelcro.paywall.incrementPageViewFrequency(true);
    }
}

/**
 * @returns boolean
 */
export function canUserDisablePaywall(checkViews = true) {
    let hasEntitlements = false;
    let hasSubscription = isUserSubscribed();
    let hasPageViews = false;

    console.log(`Paywall Render: check subscription ${hasSubscription}`);

    if (pelcro_details && 'paywall_entitlement' in pelcro_details && pelcro_details.paywall_entitlement !== '') {
        hasEntitlements = isUserEntitledTo(`${pelcro_details.paywall_entitlement}`);
        console.log(`Paywall Render: check entitlement ${pelcro_details.paywall_entitlement} ${hasEntitlements}`);
    }

    if (pelcro_details && 'paywall_type' in pelcro_details && pelcro_details.paywall_type === 'soft') {
        const pageViews = getPaywallPageViewsLeft();
        hasPageViews = getPaywallPageViewsLeft() > 0;
        console.log(
            `Paywall Render: check page views for type ${pelcro_details.paywall_type} ${hasPageViews} views ${pageViews}`,
        );
    }

    return checkViews ? hasSubscription || hasEntitlements || hasPageViews : hasSubscription || hasEntitlements;
}

/**
 * @returns boolean
 */
export function displayPaywallType(paywallType, paywallEntitlement) {
    let shouldPaywall = false;
    if (paywallType !== '') {
        // Need to ensure we set the meta tag if not added or won't be able to fetch the number of free visists left
        addPaywallTag(`${paywallType}`);

        const isSubscribed = isUserSubscribed();
        const isEntitled = paywallEntitlement !== '' && isUserEntitledTo(`${paywallEntitlement}`);
        const hasPageViews = getPaywallPageViewsLeft() > 0;

        if (paywallType === 'soft') {
            shouldPaywall = !isSubscribed && !isEntitled && !hasPageViews;
        } else if (paywallType === 'hard') {
            shouldPaywall = !isSubscribed && !isEntitled;
        }
    }

    return shouldPaywall;
}

/**
 * You should pass hard, soft or the sku for the offer if there is one configured
 *
 * @param {string} tag
 * @returns void
 */
export function addPaywallTag(tag) {
    // Need to add the tag first in order to trigger the modal
    if (document.querySelectorAll(`meta[content="${tag}"]`).length <= 0) {
        var meta = document.createElement('meta');
        meta.setAttribute('property', 'article:tag');
        meta.content = tag;
        document.getElementsByTagName('head')[0].appendChild(meta);
        console.log(`Paywall Render: trigger ${tag}`);
    }
}

/**
 * This will add the paywall header tags to the site head
 */
export function addPaywallTags() {
    console.log('Paywall Render: add paywall tags');
    if (
        pelcro_details &&
        'paywall_tags' in pelcro_details &&
        Array.isArray(pelcro_details.paywall_tags) &&
        pelcro_details.paywall_tags.length > 0
    ) {
        console.log(`Paywall Render: found paywall tags ${pelcro_details.paywall_tags.length}`);
        for (let i = 0; i < pelcro_details.paywall_tags.length; i++) {
            addPaywallTag(`${pelcro_details.paywall_tags[i]}`);
        }
    }
}

/**
 * If logged in need to check once the user data is loaded
 */
export function renderPaywall() {
    // Will need to wait till the user object has loaded before able to check if should display paywall tags
    console.log('Paywall Render: init');

    if (hasAuthToken()) {
        console.log('Paywall Render: has auth token');
        document.addEventListener('PelcroUserLoaded', () => {
            console.log('Paywall Render: PelcroUserLoaded event triggered');
            if (!canUserDisablePaywall(false)) {
                addPaywallTags();
            }
        });
    } else {
        console.log('Paywall Render: no auth token');
        document.addEventListener('PelcroSiteLoaded', () => {
            console.log('Paywall Render: PelcroSiteLoaded event triggered');
            addPaywallTags();
        });
    }
}

/**
 * Fetch the user country and currency based on specifc country
 *
 * @returns {object}
 */
export async function getUserGeoData() {
    let country = document.cookie.match('(^|;)\\s*geo-country\\s*=\\s*([^;]+)')?.pop() || '';
    let currency = document.cookie.match('(^|;)\\s*geo-currency\\s*=\\s*([^;]+)')?.pop() || '';
    let locale = document.cookie.match('(^|;)\\s*geo-locale\\s*=\\s*([^;]+)')?.pop() || '';

    if (country === '' || currency === '' || locale === '') {
        await fetch(`${wp_ajax.ajax_url}?action=getUserGeoData`)
            .then(response => response.json())
            .then(data => {
                if ('country' in data) {
                    country = data.country;
                }

                if ('currency' in data) {
                    currency = data.currency;
                }

                if ('locale' in data) {
                    locale = data.locale;
                }
            })
            .catch(error => {
                console.error('Get User GEO Data: ', error);
            });

        if (country !== '') {
            document.cookie = `geo-country=${country}; path=/; Secure`;
        }

        if (currency !== '') {
            document.cookie = `geo-currency=${currency}; path=/; Secure`;
        }

        if (locale !== '') {
            document.cookie = `geo-locale=${locale}; path=/; Secure`;
        }
    }

    return {
        country: country,
        currency: currency,
        locale: locale,
    };
}

/**
 * Formats the price based on specifc currency and locale
 *
 * @param {string} currency
 * @param {string} country
 * @returns {string}
 */
export function getGeoPrice(prices = [], currency = 'GBP', country = 'GB') {
    let price = null;
    const ukCountryCodes = 'uk_country_codes' in pelcro_details ? pelcro_details.uk_country_codes : ['GB'];
    if (isUserLoggedIn()) {
        // GBP is both UK and ROW prices we need to check the user country
        if (currency === 'GBP') {
            if (ukCountryCodes.includes(country)) {
                price = prices.find(
                    geoPrice => geoPrice.currency.toUpperCase() === 'GBP' && geoPrice.country.toUpperCase() === 'GB',
                );
            } else {
                price = prices.find(geoPrice => geoPrice.currency.toUpperCase() === 'GBP' && geoPrice.country === '');
            }
        } else {
            price = prices.find(geoPrice => geoPrice.currency.toUpperCase() === currency);
        }
    } else {
        price = prices.find(geoPrice => geoPrice.country.toUpperCase() === country);

        // If there are no SKUs in the users country then will need to display SKUs with no country
        if (price === null || typeof price === 'undefined') {
            price = prices.find(geoPrice => geoPrice.country === '');
        }
    }

    const locale =
        'locale' in Intl.NumberFormat().resolvedOptions() ? Intl.NumberFormat().resolvedOptions().locale : 'en-GB';

    return typeof price === 'object' ? formatPrice(price?.price, price?.currency, locale) : '';
}

/**
 * Formats the price based on specifc currency and locale
 *
 * @param {string} price
 * @param {string} currency
 * @param {string} locale
 * @returns {string}
 */
export function formatPrice(price, currency = 'GBP', locale = 'en-GB') {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
    });

    return `${formatter.format(price)}`;
}

/**
 * Filter Pelcro eCommerce SKUs based on users country
 *
 * @param {array} skus
 * @param {string} currency
 * @param {string} country
 * @returns {string}
 */
export function filterGEOProductSKUs(skus = [], currency = 'GBP', country = 'GB') {
    let filteredSkus = skus.filter(
        sku => sku.countriesAvailable.length > 0 && sku.countriesAvailable.includes(country),
    );

    // Sort SKu's so print is first
    filteredSkus.sort((a, b) => a.name.localeCompare(b.name)).reverse();

    return filteredSkus;
}

/**
 * Filter Pelcro subscription plans based on users country
 *
 * @param {array} plans
 * @param {string} currency
 * @param {string} country
 * @returns {string}
 */
export function filterGEOProductPlans(plans = [], currency = 'GBP', country = 'GB') {
    let filteredPlans = plans.filter(
        plan => Array.isArray(plan.countries) && plan.countries.length > 0 && plan.countries.includes(country),
    );

    return filteredPlans;
}

/**
 * @param {string} message
 * @returns
 */
export async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

/**
 * Get issue page number
 *
 * @returns {string|integer}
 */
export function getIssuePageNumber() {
    let issuePageNumber = '';
    if (pelcro_details && 'issue_page_number' in pelcro_details && pelcro_details.issue_page_number != '') {
        issuePageNumber = parseInt(pelcro_details.issue_page_number);
    }

    return issuePageNumber;
}

/**
 * @param {string} event
 * @param {array} items
 */
export function ga4TrackingEcommerce(event, items) {
    if (Array.isArray(items) && items.length > 0) {
        let total = items.reduce((prevItem, currentItem) => {
            const quantity = 'quantity' in currentItem && currentItem?.quantity ? currentItem?.quantity : 1;
            return prevItem + currentItem.price * quantity;
        }, 0);

        // If from Pelcro as Stripe doesn't store prices as decimals so need to divide by 100 e.g. 19.99 is stored as 1999
        if (total > 0 && total % 1 == 0) {
            total = total / 100;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: event,
            affiliation: 'Pelcro',
            type: 'ecommerce',
            ecommerce: {
                currency: items[0].currency.toUpperCase(),
                value: parseFloat(total.toFixed(2)),
                items: items.map(item => {
                    // Need to handle fetching data from OpenSearch or Pelcro data
                    let category =
                        'inventory_description' in item &&
                        item?.inventory_description !== null &&
                        item.inventory_description !== ''
                            ? item?.inventory_description
                            : '';
                    if ('description' in item && item?.description !== null && item?.description !== '') {
                        category = item?.description;
                    }

                    let inStock = true;
                    if ('inStock' in item && item?.inStock !== null) {
                        inStock = item?.inStock;
                    } else if ('inventory_type' in item && item?.inventory_type !== null) {
                        inStock = item.inventory_type != 1 || (item.inventory_type == 1 && item.inventory_quantity > 0);
                    }

                    let price = item.price;
                    // If from Pelcro as Stripe doesn't store prices as decimals so need to divide by 100 e.g. 19.99 is stored as 1999
                    if (item.price > 0 && item.price % 1 == 0) {
                        price = item.price / 100;
                    }

                    return {
                        item_id: item.id,
                        item_name: item.name,
                        item_category: category,
                        item_variant: item.name,
                        item_in_stock: inStock ? 'yes' : 'no',
                        price: price,
                        quantity: 'quantity' in item && item?.quantity ? item?.quantity : 1,
                    };
                }),
            },
        });
    }
}

/**
 * @param {string} event
 * @param {array} items
 */
export function ga4TrackingSubscription(event, items) {
    if (Array.isArray(items) && items.length > 0) {
        let total = items.reduce((prevItem, currentItem) => prevItem + currentItem.amount, 0);
        total = total > 0 ? total / 100 : 0;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: event,
            affiliation: 'Pelcro',
            type: 'subscription',
            ecommerce: {
                currency: items[0].currency.toUpperCase(),
                value: parseFloat(total.toFixed(2)),
                items: items.map(item => {
                    const price = item.amount > 0 ? item.amount / 100 : 0;

                    return {
                        item_id: item.id,
                        item_name: item.name_internal,
                        item_category: item.description,
                        item_variant: item.nickname,
                        item_in_stock: 'yes',
                        price: price,
                        quantity: 1,
                    };
                }),
            },
        });
    }
}

/**
 * @param {string|integer} pageNumber
 */
export function ga4TrackingPaywallBlocked(pageNumber = '') {
    const paywallType = getPaywallType();
    const viewsLeft = getPaywallFreeVisitsLeft();
    const issuePageNumber = getIssuePageNumber();
    let data = {
        event: 'content_paywall_blocked',
        contentRestricted: 'true',
        paywallBannerType: `${paywallType}`,
        paywallFreeViewLeft: paywallType == 'none' ? 'na' : viewsLeft > 0 ? viewsLeft : 0,
    };

    if (!isNaN(pageNumber) && pageNumber !== '') {
        data.issuePageNumber = parseInt(pageNumber);
    } else if (!isNaN(issuePageNumber) && issuePageNumber !== '') {
        data.issuePageNumber = issuePageNumber;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
}

/**
 * @param {string|integer} pageNumber
 */
export function ga4TrackingPaywallNotBlocked(pageNumber = '') {
    const paywallType = getPaywallType();
    const viewsLeft = getPaywallFreeVisitsLeft();
    const issuePageNumber = getIssuePageNumber();

    let data = {
        event: 'content_paywall_not_blocked',
        contentRestricted: 'false',
        paywallBannerType: `${paywallType}`,
        paywallFreeViewLeft: paywallType == 'none' ? 'na' : viewsLeft > 0 ? viewsLeft : 0,
    };

    if (!isNaN(pageNumber) && pageNumber !== '') {
        data.issuePageNumber = parseInt(pageNumber);
    } else if (!isNaN(issuePageNumber) && issuePageNumber !== '') {
        data.issuePageNumber = issuePageNumber;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
}
